import { ofType } from 'redux-observable';
import { IAction, ICountry } from '../../../helpers/interfaces';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { currencyUtils, getLoginRequestHeader, localStorageHelper } from '../../../utils';
import { from, Observable, of } from 'rxjs';
import {
    addGameToCampaignSuccess,
    addPlayerToCampaignCsvSuccess,
    cloneCampaignSuccess,
    createAwardedPromotionSuccess,
    createFreeRoundsPromotionSuccess,
    deleteCampaignSuccess,
    editAwardedCampaignSuccess,
    editFreeRoundsCampaignSuccess,
    getAwardedCampaignGamesSuccess,
    getAwardedCampaignPlayersSuccess,
    getAwardedCampaignSuccess,
    getAwardedPromotionsSuccess,
    getFreeRoundsCampaignGamesSuccess,
    getFreeRoundsCampaignPlayersSuccess,
    getFreeRoundsCampaignSuccess,
    getFreeRoundsGamesByPromotionPriceSuccess,
    getFreeRoundsPromotionsSuccess,
    getAwardedGamesByPromotionPriceSuccess,
    importPlayersCsvSuccess,
    PromotionTypes,
    removeGameFromCampaignSuccess,
    removePlayersFromCampaignSuccess,
    terminateCampaignSuccess,
    exportPlayersCsvSuccess,
} from '../../actions/backoffice/promotions-actions';
import axios from 'axios';
import { apiError } from '../../actions/user-actions';
import { SERVER_URL } from '../../../utils/config';
import { OPERATOR_CAMPAIGN_OWNER } from '../../../BackOffice/Promotions/promotionUtils';
import Papa from 'papaparse';
import FormData from 'form-data';
import { formatCampaignDateTimeForApi } from '../../../BackOffice/Promotions/promotionDateTime';

const countriesList =
    localStorageHelper
        .getChainedValue('user.countries')
        ?.map((country: ICountry) => country.alpha2Code) || [];

export function createBinaryCsvFile(players: string[]): Blob {
    if (!Array.isArray(players) || players.length === 0) {
        throw new Error("Invalid data: 'players' must be a non-empty array.");
    }

    const formattedPlayers = players.map((name) => [name]);

    try {
        const csvContent = Papa.unparse(formattedPlayers, { header: false });

        return new Blob([csvContent], { type: 'text/csv' });
    } catch (error) {
        throw new Error('Failed to create binary CSV file.');
    }
}

const normalizePlayers = (players: any): string[] => {
    if (!Array.isArray(players)) {
        return [];
    }

    return Array.from(
        new Set(
            players
                .map((player) => String(player || '').trim())
                .filter(Boolean),
        ),
    );
};

const exportPlayersToCsvFile = (campaignId: string | number, players: any[]) => {
    const csvHeaders = [
        'ID',
        'Player ID',
        'Received At',
        'Total Bet (USD)',
        'Total Win (USD)',
        'Played Features',
        'Last Round Date',
        'Status',
    ];

    const csvRows = players.map((player: any) => [
        player.id || '',
        player.playerId || '',
        player.receivedAt || '',
        player.totalBet || '0',
        player.totalWin || '0',
        player.roundsPlayed || '0',
        player.lastRoundDate || '',
        player.status || '',
    ]);

    const csvData = [csvHeaders, ...csvRows];
    const csvContent = csvData
        .map((row) =>
            row
                .map((cell: any) => {
                    const cellStr = String(cell || '');
                    if (
                        cellStr.includes(',') ||
                        cellStr.includes('"') ||
                        cellStr.includes('\n')
                    ) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }

                    return cellStr;
                })
                .join(','),
        )
        .join('\n');

    const csvWithBOM = '\uFEFF' + csvContent;
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = downloadUrl;
    a.download = `players_export_${campaignId}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

export const getFreeRoundsPromotionsEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_FREE_ROUNDS_PROMOTIONS_REQUEST),
        mergeMap((action) => {
            const {
                paginationData,
                sortConfig,
                filterString,
                filterKey,
                dateRange,
                currency,
                status,
                owner,
            } = action.payload;

            const params = new URLSearchParams();
            params.append('paging.pageNumber', paginationData.pageNumber.toString());
            params.append('paging.pageSize', paginationData.pageSize.toString());
            params.append('paging.sortBy', sortConfig.sortKey);
            params.append('paging.order', sortConfig.sortOrder);
            params.append('type', 'FREE_ROUNDS');

            if (filterString) {
                params.append(filterKey, filterString);
            }
            if (status) {
                params.append('status', status);
            }
            if (currency) {
                params.append('currencyCode', currency);
            }
            if (owner) {
                params.append('owner', owner);
            }
            if (dateRange[0]) {
                params.append('startDateFrom', dateRange[0].format('YYYY-MM-DD'));
            }
            if (dateRange[1]) {
                params.append('startDateTo', dateRange[1].format('YYYY-MM-DD'));
            }

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.get(`${SERVER_URL}/api/v1/brand/current/campaign`, {
                    params: params,
                    headers: headers,
                }),
            ).pipe(
                map((response) => getFreeRoundsPromotionsSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_FREE_ROUNDS_PROMOTIONS_REQUEST)),
                ),
            );
        }),
    );

export const getAwardedPromotionsEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_AWARDED_PROMOTIONS_REQUEST),
        mergeMap((action) => {
            const {
                paginationData,
                sortConfig,
                filterString,
                filterKey,
                dateRange,
                currency,
                status,
                owner,
            } = action.payload;

            const params = new URLSearchParams();
            params.append('paging.pageNumber', paginationData.pageNumber.toString());
            params.append('paging.pageSize', paginationData.pageSize.toString());
            params.append('paging.sortBy', sortConfig.sortKey);
            params.append('paging.order', sortConfig.sortOrder);
            params.append('type', 'AWARDED_FEATURE');

            if (filterString) {
                params.append(filterKey, filterString);
            }
            if (status) {
                params.append('status', status);
            }
            if (currency) {
                params.append('currencyCode', currency);
            }
            if (owner) {
                params.append('owner', owner);
            }
            if (dateRange[0]) {
                params.append('startDateFrom', dateRange[0].format('YYYY-MM-DD'));
            }
            if (dateRange[1]) {
                params.append('startDateTo', dateRange[1].format('YYYY-MM-DD'));
            }

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.get(`${SERVER_URL}/api/v1/brand/current/campaign`, {
                    params: params,
                    headers: headers,
                }),
            ).pipe(
                map((response) => getAwardedPromotionsSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_AWARDED_PROMOTIONS_REQUEST)),
                ),
            );
        }),
    );

export const createFreeRoundsPromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.CREATE_FREE_ROUNDS_PROMOTION_REQUEST),
        mergeMap((action) => {
            const { payload } = action;

            const requestBody: any = {
                name: payload.campaign_name,
                gameIds: payload.gameIds,
                startDate: formatCampaignDateTimeForApi(payload.start_at, payload.timezone),
                endDate: formatCampaignDateTimeForApi(payload.end_at, payload.timezone),
                timezone: payload.timezone,
                bet: Number(payload.price),
                currencyCode: payload.currency,
                countries: countriesList,
                strategy: payload.strategy,
                betNumber: Number(payload.number_of_features),
                type: 'FREE_ROUNDS',
            };

            if (payload.strategy === 'FIRST_N_PLAYERS') {
                requestBody.eligiblePlayersNumber = payload.number_of_players;
            }

            if (payload.rulesUrl) {
                requestBody.rulesUrl = payload.rulesUrl;
            }

            if (payload.valid_for > 0) {
                requestBody.startEvent = payload.start_event;
                requestBody.validForDays = payload.valid_for;
                requestBody.allowPostExpirationCompletion = payload.allowPostExpirationCompletion;
            }

            if (payload.externalCampaignId) {
                requestBody.externalCampaignId = payload.externalCampaignId;
            }

            if (payload.promotionSettings) {
                requestBody.promotionSettings = payload.promotionSettings;
            }

            requestBody.owner = payload.owner || OPERATOR_CAMPAIGN_OWNER;

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.post(`${SERVER_URL}/api/v1/brand/current/campaign`, requestBody, { headers }),
            ).pipe(
                switchMap((response) => {
                    const campaignId = response.data.id;

                    if (payload.strategy === 'PLAYER_LIST') {
                        const csvFile = createBinaryCsvFile(payload.players);
                        const formData = new FormData();

                        formData.append('file', csvFile, 'participants.csv');

                        return from(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant-file`,
                                formData,
                                {
                                    headers: {
                                        ...getLoginRequestHeader(),
                                    },
                                },
                            ),
                        ).pipe(
                            map(() => createFreeRoundsPromotionSuccess(response.data)),
                            catchError((error) =>
                                of(
                                    apiError(
                                        error,
                                        PromotionTypes.CREATE_FREE_ROUNDS_PROMOTION_REQUEST,
                                    ),
                                ),
                            ),
                        );
                    }

                    return of(createFreeRoundsPromotionSuccess(response.data));
                }),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.CREATE_FREE_ROUNDS_PROMOTION_REQUEST)),
                ),
            );
        }),
    );

export const createAwardedPromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.CREATE_AWARDED_PROMOTION_REQUEST),
        mergeMap((action) => {
            const { payload } = action;

            const requestBody: any = {
                name: payload.campaign_name,
                gameIds: payload.gameIds,
                startDate: formatCampaignDateTimeForApi(payload.start_at, payload.timezone),
                endDate: formatCampaignDateTimeForApi(payload.end_at, payload.timezone),
                timezone: payload.timezone,
                bet: Number(payload.price),
                currencyCode: payload.currency,
                countries: countriesList,
                strategy: payload.strategy,
                betNumber: Number(payload.number_of_features),
                type: 'AWARDED_FEATURE',
            };

            if (payload.strategy === 'FIRST_N_PLAYERS') {
                requestBody.eligiblePlayersNumber = payload.number_of_players;
            }

            if (payload.rulesUrl) {
                requestBody.rulesUrl = payload.rulesUrl;
            }

            if (payload.valid_for > 0) {
                requestBody.startEvent = payload.start_event;
                requestBody.validForDays = payload.valid_for;
                requestBody.allowPostExpirationCompletion = payload.allowPostExpirationCompletion;
            }

            if (payload.externalCampaignId) {
                requestBody.externalCampaignId = payload.externalCampaignId;
            }

            if (payload.promotionSettings) {
                requestBody.promotionSettings = payload.promotionSettings;
            }

            requestBody.owner = payload.owner || OPERATOR_CAMPAIGN_OWNER;

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.post(`${SERVER_URL}/api/v1/brand/current/campaign`, requestBody, { headers }),
            ).pipe(
                switchMap((response) => {
                    const campaignId = response.data.id;

                    if (payload.strategy === 'PLAYER_LIST') {
                        const csvFile = createBinaryCsvFile(payload.players);
                        const formData = new FormData();

                        formData.append('file', csvFile, 'participants.csv');

                        return from(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant-file`,
                                formData,
                                {
                                    headers: {
                                        ...getLoginRequestHeader(),
                                    },
                                },
                            ),
                        ).pipe(
                            map(() => createAwardedPromotionSuccess(response.data)),
                            catchError((error) =>
                                of(
                                    apiError(
                                        error,
                                        PromotionTypes.CREATE_AWARDED_PROMOTION_REQUEST,
                                    ),
                                ),
                            ),
                        );
                    }

                    return of(createAwardedPromotionSuccess(response.data));
                }),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.CREATE_AWARDED_PROMOTION_REQUEST)),
                ),
            );
        }),
    );

export const getFreeRoundsCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const { campaignId, currency } = action.payload;
            const headers = getLoginRequestHeader('application/json');

            return from(
                (async () => {
                    let finalCurrency = currency;

                    const campaignDataResponse = await axios.get(
                        `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}`,
                        { headers },
                    );

                    if (currency === 'user') {
                        finalCurrency = currencyUtils.getActiveCurrencyData().code;
                    } else if (currency === 'campaign') {
                        finalCurrency = campaignDataResponse.data.currencyCode;
                    }

                    const summaryResponse = await axios.get(
                        `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/summary`,
                        { headers, params: { currency: finalCurrency } },
                    );

                    return getFreeRoundsCampaignSuccess({
                        campaignData: campaignDataResponse.data,
                        summary: summaryResponse.data,
                    });
                })(),
            ).pipe(
                mergeMap((response) => of(response)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const getAwardedCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_AWARDED_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const { campaignId, currency } = action.payload;
            const headers = getLoginRequestHeader('application/json');

            return from(
                (async () => {
                    let finalCurrency = currency;

                    const campaignDataResponse = await axios.get(
                        `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}`,
                        { headers },
                    );

                    if (currency === 'user') {
                        finalCurrency = currencyUtils.getActiveCurrencyData().code;
                    } else if (currency === 'campaign') {
                        finalCurrency = campaignDataResponse.data.currencyCode;
                    }

                    const summaryResponse = await axios.get(
                        `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/summary`,
                        { headers, params: { currency: finalCurrency } },
                    );

                    return getAwardedCampaignSuccess({
                        campaignData: campaignDataResponse.data,
                        summary: summaryResponse.data,
                    });
                })(),
            ).pipe(
                mergeMap((response) => of(response)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_AWARDED_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const deletePromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.DELETE_CAMPAIGN_REQUEST),
        mergeMap(async (action) => {
            const headers = getLoginRequestHeader('application/json');

            try {
                const response = await axios.delete(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload}`,
                    { headers },
                );
                return deleteCampaignSuccess(response.data);
            } catch (error) {
                return apiError(error, PromotionTypes.DELETE_CAMPAIGN_REQUEST);
            }
        }),
    );

export const terminatePromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.TERMINATE_CAMPAIGN_REQUEST),
        mergeMap(async (action) => {
            const requestBody = {
                enabled: false,
                force: action.payload.force,
            };

            const headers = getLoginRequestHeader('application/json');

            try {
                const response = await axios.put(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}/enabled`,
                    requestBody,
                    { headers },
                );
                return terminateCampaignSuccess(response.data);
            } catch (error) {
                return apiError(error, PromotionTypes.TERMINATE_CAMPAIGN_REQUEST);
            }
        }),
    );

export const getAwardedGamesByPromotionPriceEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_AWARDED_GAMES_BY_PROMOTION_PRICE_REQUEST),
        mergeMap((action) => {
            const { price, currencyId } = action.payload;
            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.get(
                    `${SERVER_URL}/games/awarded-feature?currencyId=${currencyId}&price=${price}`,
                    {
                        headers: headers,
                    },
                ),
            ).pipe(
                map((response) => getAwardedGamesByPromotionPriceSuccess(response.data)),
                catchError((error) =>
                    of(
                        apiError(
                            error,
                            PromotionTypes.GET_AWARDED_GAMES_BY_PROMOTION_PRICE_REQUEST,
                        ),
                    ),
                ),
            );
        }),
    );

export const getFreeRoundsGamesByPromotionPriceEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_FREE_ROUNDS_GAMES_BY_PROMOTION_PRICE_REQUEST),
        mergeMap((action) => {
            const { price, currencyId } = action.payload;
            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.get(
                    `${SERVER_URL}/games/free-round?currencyId=${currencyId}&bet=${price}`,
                    {
                        headers: headers,
                    },
                ),
            ).pipe(
                map((response) => getFreeRoundsGamesByPromotionPriceSuccess(response.data)),
                catchError((error) =>
                    of(
                        apiError(
                            error,
                            PromotionTypes.GET_FREE_ROUNDS_GAMES_BY_PROMOTION_PRICE_REQUEST,
                        ),
                    ),
                ),
            );
        }),
    );

export const cloneCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.CLONE_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const campaignId = action.payload;
            const headers = getLoginRequestHeader('application/json');

            const fetchCampaignData = axios.get(
                `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}`,
                { headers },
            );

            return from(fetchCampaignData).pipe(
                mergeMap((campaignDataResponse) => {
                    const shouldRequestCsv = campaignDataResponse.data.strategy === 'PLAYER_LIST';

                    const fetchCsv = shouldRequestCsv
                        ? axios.get(
                              `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant/csv`,
                              { headers },
                          )
                        : Promise.resolve({ data: null });

                    return from(fetchCsv).pipe(
                        mergeMap((csvResponse) =>
                            of(
                                cloneCampaignSuccess({
                                    campaignData: campaignDataResponse.data,
                                    csv: csvResponse.data,
                                }),
                            ),
                        ),
                    );
                }),
                catchError((error) => of(apiError(error, PromotionTypes.CLONE_CAMPAIGN_REQUEST))),
            );
        }),
    );

export const getAwardedCampaignPlayersEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_AWARDED_CAMPAIGN_PLAYERS_REQUEST),
        mergeMap((action) => {
            const {
                campaignId,
                paginationData,
                sortKey,
                sortOrder,
                playerIdSearchValue,
                playersStatus,
                currency,
            } = action.payload;

            const headers = getLoginRequestHeader('application/json');
            const playersConfig = {
                headers,
                params: {
                    currency,
                    'paging.pageNumber': paginationData.pageNumber,
                    'paging.pageSize': paginationData.pageSize,
                    'paging.sortBy': sortKey,
                    'paging.order': sortOrder,
                    ...(playerIdSearchValue ? { playerId: playerIdSearchValue } : {}),
                    ...(playersStatus !== 'all' ? { status: playersStatus } : {}),
                },
            };

            return from(
                axios.get(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant`,
                    playersConfig,
                ),
            ).pipe(
                map(({ data }) => getAwardedCampaignPlayersSuccess(data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_AWARDED_CAMPAIGN_PLAYERS_REQUEST)),
                ),
            );
        }),
    );

export const getFreeRoundsCampaignPlayersEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_PLAYERS_REQUEST),
        mergeMap((action) => {
            const {
                campaignId,
                paginationData,
                sortKey,
                sortOrder,
                playerIdSearchValue,
                playersStatus,
                currency,
            } = action.payload;

            const headers = getLoginRequestHeader('application/json');
            const playersConfig = {
                headers,
                params: {
                    currency,
                    'paging.pageNumber': paginationData.pageNumber,
                    'paging.pageSize': paginationData.pageSize,
                    'paging.sortBy': sortKey,
                    'paging.order': sortOrder,
                    ...(playerIdSearchValue ? { playerId: playerIdSearchValue } : {}),
                    ...(playersStatus !== 'all' ? { status: playersStatus } : {}),
                },
            };

            return from(
                axios.get(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant`,
                    playersConfig,
                ),
            ).pipe(
                map(({ data }) => getFreeRoundsCampaignPlayersSuccess(data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_PLAYERS_REQUEST)),
                ),
            );
        }),
    );

export const editFreeRoundsPromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.EDIT_FREE_ROUNDS_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const { payload } = action;

            const requestBody: any = {
                name: payload.campaign_name,
                gameIds: payload.gameIds,
                startDate: formatCampaignDateTimeForApi(payload.start_at, payload.timezone),
                endDate: formatCampaignDateTimeForApi(payload.end_at, payload.timezone),
                timezone: payload.timezone,
                bet: payload.price,
                currencyCode: payload.currency,
                betNumber: payload.number_of_features,
                countries: countriesList,
                strategy: payload.strategy,
                rulesUrl: payload.rulesUrl,
                type: 'FREE_ROUNDS',
            };

            if (payload.strategy === 'FIRST_N_PLAYERS') {
                requestBody.eligiblePlayersNumber = payload.number_of_players;
            }

            if (payload.valid_for > 0) {
                requestBody.startEvent = payload.start_event;
                requestBody.validForDays = payload.valid_for;
                requestBody.allowPostExpirationCompletion = payload.allowPostExpirationCompletion;
            }

            if (payload.externalCampaignId) {
                requestBody.externalCampaignId = payload.externalCampaignId;
            }

            if (payload.promotionSettings) {
                requestBody.promotionSettings = payload.promotionSettings;
            }

            if (payload.owner) {
                requestBody.owner = payload.owner;
            }

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.put(`${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}`, requestBody, {
                    headers: headers,
                }),
            ).pipe(
                switchMap((response) => {
                    if (payload.strategy !== 'PLAYER_LIST') {
                        return of(editFreeRoundsCampaignSuccess(response.data));
                    }

                    const originalPlayers = normalizePlayers(payload.originalPlayers);
                    const currentPlayers = normalizePlayers(payload.players);
                    const originalSet = new Set(originalPlayers);
                    const currentSet = new Set(currentPlayers);
                    const playersToAdd = currentPlayers.filter((player) => !originalSet.has(player));
                    const playersToRemove = originalPlayers.filter((player) => !currentSet.has(player));
                    const participantRequests: Array<Promise<any>> = [];

                    if (playersToAdd.length > 0) {
                        const csvFile = createBinaryCsvFile(playersToAdd);
                        const formData = new FormData();
                        formData.append('file', csvFile, 'participants.csv');

                        participantRequests.push(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}/participant-file`,
                                formData,
                                {
                                    headers: {
                                        ...getLoginRequestHeader(),
                                    },
                                },
                            ),
                        );
                    }

                    if (playersToRemove.length > 0) {
                        participantRequests.push(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}/participant-removing`,
                                { playerIds: playersToRemove },
                                { headers: getLoginRequestHeader() },
                            ),
                        );
                    }

                    if (participantRequests.length === 0) {
                        return of(editFreeRoundsCampaignSuccess(response.data));
                    }

                    return from(Promise.all(participantRequests)).pipe(
                        map(() => editFreeRoundsCampaignSuccess(response.data)),
                        catchError((error) =>
                            of(apiError(error, PromotionTypes.EDIT_FREE_ROUNDS_CAMPAIGN_REQUEST)),
                        ),
                    );
                }),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.EDIT_FREE_ROUNDS_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const editAwardedPromotionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.EDIT_AWARDED_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const { payload } = action;

            const requestBody: any = {
                name: payload.campaign_name,
                gameIds: payload.gameIds,
                startDate: formatCampaignDateTimeForApi(payload.start_at, payload.timezone),
                endDate: formatCampaignDateTimeForApi(payload.end_at, payload.timezone),
                timezone: payload.timezone,
                bet: payload.price,
                currencyCode: payload.currency,
                betNumber: payload.number_of_features,
                countries: countriesList,
                strategy: payload.strategy,
                rulesUrl: payload.rulesUrl,
                type: 'AWARDED_FEATURE',
            };

            if (payload.strategy === 'FIRST_N_PLAYERS') {
                requestBody.eligiblePlayersNumber = payload.number_of_players;
            }

            if (payload.valid_for > 0) {
                requestBody.startEvent = payload.start_event;
                requestBody.validForDays = payload.valid_for;
                requestBody.allowPostExpirationCompletion = payload.allowPostExpirationCompletion;
            }

            if (payload.externalCampaignId) {
                requestBody.externalCampaignId = payload.externalCampaignId;
            }

            if (payload.promotionSettings) {
                requestBody.promotionSettings = payload.promotionSettings;
            }

            if (payload.owner) {
                requestBody.owner = payload.owner;
            }

            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.put(`${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}`, requestBody, {
                    headers: headers,
                }),
            ).pipe(
                switchMap((response) => {
                    if (payload.strategy !== 'PLAYER_LIST') {
                        return of(editAwardedCampaignSuccess(response.data));
                    }

                    const originalPlayers = normalizePlayers(payload.originalPlayers);
                    const currentPlayers = normalizePlayers(payload.players);
                    const originalSet = new Set(originalPlayers);
                    const currentSet = new Set(currentPlayers);
                    const playersToAdd = currentPlayers.filter((player) => !originalSet.has(player));
                    const playersToRemove = originalPlayers.filter((player) => !currentSet.has(player));
                    const participantRequests: Array<Promise<any>> = [];

                    if (playersToAdd.length > 0) {
                        const csvFile = createBinaryCsvFile(playersToAdd);
                        const formData = new FormData();
                        formData.append('file', csvFile, 'participants.csv');

                        participantRequests.push(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}/participant-file`,
                                formData,
                                {
                                    headers: {
                                        ...getLoginRequestHeader(),
                                    },
                                },
                            ),
                        );
                    }

                    if (playersToRemove.length > 0) {
                        participantRequests.push(
                            axios.post(
                                `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.id}/participant-removing`,
                                { playerIds: playersToRemove },
                                { headers: getLoginRequestHeader() },
                            ),
                        );
                    }

                    if (participantRequests.length === 0) {
                        return of(editAwardedCampaignSuccess(response.data));
                    }

                    return from(Promise.all(participantRequests)).pipe(
                        map(() => editAwardedCampaignSuccess(response.data)),
                        catchError((error) =>
                            of(apiError(error, PromotionTypes.EDIT_AWARDED_CAMPAIGN_REQUEST)),
                        ),
                    );
                }),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.EDIT_AWARDED_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const importCsvFileEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.IMPORT_PLAYERS_CSV_REQUEST),
        mergeMap((action) => {
            const headers = {
                ...getLoginRequestHeader(),
                'Content-Type': 'multipart/form-data',
            };

            const formData = new FormData();
            formData.append('file', action.payload.file);

            return from(
                axios.post(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.campaignId}/participant-file`,
                    formData,
                    { headers },
                ),
            ).pipe(
                map((response) => importPlayersCsvSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.IMPORT_PLAYERS_CSV_REQUEST)),
                ),
            );
        }),
    );

export const addPlayerToCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.ADD_PLAYER_TO_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();

            const fromPromise = from(
                axios.post(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.campaignId}/participant`,
                    { playerId: action.payload.playerId },
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => addPlayerToCampaignCsvSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.ADD_PLAYER_TO_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const removePlayersFromCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.REMOVE_PLAYERS_FROM_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();

            const fromPromise = from(
                axios.post(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.campaignId}/participant-removing`,
                    { playerIds: action.payload.playerIds },
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => removePlayersFromCampaignSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.REMOVE_PLAYERS_FROM_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const getAwardedCampaignGamesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_AWARDED_CAMPAIGN_GAMES_REQUEST),
        mergeMap((action) => {
            const { campaignId, paginationData, sortConfig, gameNameSearchValue } = action.payload;

            const headers = getLoginRequestHeader('application/json');
            const playersConfig = {
                headers,
                params: {
                    gameName: gameNameSearchValue,
                    'paging.pageNumber': paginationData.pageNumber,
                    'paging.pageSize': paginationData.pageSize,
                    'paging.sortBy': sortConfig.sortKey,
                    'paging.order': sortConfig.sortOrder,
                    ...(gameNameSearchValue ? { gameName: gameNameSearchValue } : {}),
                },
            };

            return from(
                axios.get(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/game`,
                    playersConfig,
                ),
            ).pipe(
                map((response) => getAwardedCampaignGamesSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_AWARDED_CAMPAIGN_GAMES_REQUEST)),
                ),
            );
        }),
    );

export const getFreeRoundsCampaignGamesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_GAMES_REQUEST),
        mergeMap((action) => {
            const { campaignId, paginationData, sortConfig, gameNameSearchValue } = action.payload;

            const headers = getLoginRequestHeader('application/json');
            const playersConfig = {
                headers,
                params: {
                    gameName: gameNameSearchValue,
                    'paging.pageNumber': paginationData.pageNumber,
                    'paging.pageSize': paginationData.pageSize,
                    'paging.sortBy': sortConfig.sortKey,
                    'paging.order': sortConfig.sortOrder,
                    ...(gameNameSearchValue ? { gameName: gameNameSearchValue } : {}),
                },
            };

            return from(
                axios.get(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/game`,
                    playersConfig,
                ),
            ).pipe(
                map((response) => getFreeRoundsCampaignGamesSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.GET_FREE_ROUNDS_CAMPAIGN_GAMES_REQUEST)),
                ),
            );
        }),
    );

export const addGameToCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.ADD_GAME_TO_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();

            const fromPromise = from(
                axios.post(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.campaignId}/game`,
                    { gameId: action.payload.gameId },
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => addGameToCampaignSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.ADD_GAME_TO_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const removeGameFromCampaignEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.REMOVE_GAME_FROM_CAMPAIGN_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader();

            const fromPromise = from(
                axios.delete(
                    `${SERVER_URL}/api/v1/brand/current/campaign/${action.payload.campaignId}/game/${action.payload.gameId}`,
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => removeGameFromCampaignSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PromotionTypes.REMOVE_GAME_FROM_CAMPAIGN_REQUEST)),
                ),
            );
        }),
    );

export const exportPlayersCsvEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PromotionTypes.EXPORT_PLAYERS_CSV_REQUEST),
        mergeMap((action) => {
            const {
                campaignId,
                players,
                paginationData,
                sortKey,
                sortOrder,
                playerIdSearchValue,
                playersStatus,
                currency,
            } = action.payload;

            // Backend CSV download (disabled but kept for reference)
            // const config = {
            //     method: 'GET',
            //     headers: {
            //         ...getLoginRequestHeader(),
            //         'Accept': 'text/csv',
            //     }
            // };
            // const url = `${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant/csv`;

            const headers = getLoginRequestHeader('application/json');
            const shouldFetchAllPlayers = Boolean(campaignId && paginationData && sortKey && sortOrder);
            const playersRequest = shouldFetchAllPlayers
                ? axios.get(`${SERVER_URL}/api/v1/brand/current/campaign/${campaignId}/participant`, {
                      headers,
                      params: {
                          currency,
                          'paging.pageNumber': paginationData.pageNumber,
                          'paging.pageSize': paginationData.pageSize,
                          'paging.sortBy': sortKey,
                          'paging.order': sortOrder,
                          ...(playerIdSearchValue ? { playerId: playerIdSearchValue } : {}),
                          ...(playersStatus && playersStatus !== 'all'
                              ? { status: playersStatus }
                              : {}),
                      },
                  })
                : Promise.resolve({ data: { content: players || [] } });

            return from(playersRequest).pipe(
                mergeMap((response) => {
                    const playersToExport = response?.data?.content || players || [];

                    if (!playersToExport.length) {
                        return of(
                            apiError(
                                new Error('No players data available for CSV export'),
                                PromotionTypes.EXPORT_PLAYERS_CSV_REQUEST,
                            ),
                            exportPlayersCsvSuccess(null),
                        );
                    }

                    try {
                        exportPlayersToCsvFile(campaignId, playersToExport);
                    } catch (error) {
                        return of(
                            apiError(error, PromotionTypes.EXPORT_PLAYERS_CSV_REQUEST),
                            exportPlayersCsvSuccess(null),
                        );
                    }

                    return of(exportPlayersCsvSuccess(null));
                }),
                catchError((error) =>
                    of(
                        apiError(error, PromotionTypes.EXPORT_PLAYERS_CSV_REQUEST),
                        exportPlayersCsvSuccess(null),
                    ),
                ),
            );
        }),
    );
